import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Plus, FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Invoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [formData, setFormData] = useState({
    campaign_id: '',
    amount: '',
    gst_percentage: '18',
    description: '',
    creator_address: '',
    creator_gstin: '',
    brand_address: '',
    brand_gstin: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, campaignsRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/campaigns`, { withCredentials: true })
      ]);
      setInvoices(invoicesRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/invoices/${invoiceId}/pdf`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${API_URL}/api/invoices`,
        {
          ...formData,
          amount: parseFloat(formData.amount),
          gst_percentage: parseFloat(formData.gst_percentage)
        },
        { withCredentials: true }
      );
      setShowCreateDialog(false);
      setShowOptionalFields(false);
      setFormData({
        campaign_id: '',
        amount: '',
        gst_percentage: '18',
        description: '',
        creator_address: '',
        creator_gstin: '',
        brand_address: '',
        brand_gstin: ''
      });
      fetchData();

      toast.success(`Invoice ${data.invoice_number} created! Downloading PDF...`, { duration: 4000 });

      // Auto download PDF after creation
      setTimeout(() => {
        downloadInvoicePDF(data.id, data.invoice_number);
      }, 300);
    } catch (error) {
      console.error('Failed to create invoice:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      // Talent Managers hit 403 when the campaign is owned by a different
      // manager. Give them a clear explanation instead of a raw API error.
      if ((status === 403 || status === 422 || status === 404) && user?.role === 'talent_manager') {
        toast.error(
          'You can only raise invoices for campaigns where your roster creator is assigned. Contact the campaign manager if needed.'
        );
      } else {
        toast.error(detail || 'Failed to create invoice');
      }
    }
  };

  const handleStatusChange = async (invoiceId, status) => {
    try {
      await axios.patch(
        `${API_URL}/api/invoices/${invoiceId}/status`,
        { status },
        { withCredentials: true }
      );
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return '#00C972';
      case 'pending':
        return '#FFB800';
      case 'overdue':
        return '#FF3B30';
      default:
        return '#999999';
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="invoices-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2">Invoices</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Generate and download invoice PDFs</p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) setShowOptionalFields(false); }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#E63E17] transition-colors focus:ring-2 focus:ring-[#FF451A]/30"
                  data-testid="create-invoice-dialog-button"
                >
                  <Plus size={20} className="mr-2" />
                  Generate Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Generate New Invoice</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Campaign</label>
                    <Select value={formData.campaign_id} onValueChange={(value) => setFormData({ ...formData, campaign_id: value })} required>
                      <SelectTrigger className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="invoice-campaign-select">
                        <SelectValue placeholder="Select a campaign" />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.length === 0 ? (
                          <div className="px-4 py-2 text-sm text-[#6B6B6B] dark:text-zinc-500">No campaigns. Create one first.</div>
                        ) : (
                          campaigns.map(campaign => (
                            <SelectItem key={campaign.id} value={campaign.id}>
                              {campaign.name} - {campaign.brand}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Amount (₹)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        required
                        placeholder="50000"
                        className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                        data-testid="invoice-amount-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">GST (%)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.gst_percentage}
                        onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.value })}
                        required
                        placeholder="18"
                        className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                        data-testid="invoice-gst-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      placeholder="Services rendered for campaign..."
                      className="w-full rounded-xl px-4 py-3 min-h-[80px] bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="invoice-description-input"
                    />
                  </div>

                  <div className="pt-2 border-t border-black/[0.08] dark:border-white/5">
                    <button
                      type="button"
                      onClick={() => setShowOptionalFields(!showOptionalFields)}
                      className="flex items-center gap-2 text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white w-full text-left py-2"
                      data-testid="toggle-optional-fields-button"
                    >
                      {showOptionalFields ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      <span className="font-medium">Add address & GSTIN details (optional)</span>
                      <span className="text-xs text-[#6B6B6B] dark:text-zinc-500 ml-auto">For GST compliance</span>
                    </button>

                    {showOptionalFields && (
                      <div className="space-y-4 pt-3">
                        <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl p-4 space-y-3">
                          <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Your Details</h4>
                          <div>
                            <Input
                              value={formData.creator_address}
                              onChange={(e) => setFormData({ ...formData, creator_address: e.target.value })}
                              placeholder="Your address (e.g., 123 Main St, Mumbai)"
                              className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                              data-testid="invoice-creator-address-input"
                            />
                          </div>
                          <div>
                            <Input
                              value={formData.creator_gstin}
                              onChange={(e) => setFormData({ ...formData, creator_gstin: e.target.value })}
                              placeholder="Your GSTIN (e.g., 22AAAAA0000A1Z5)"
                              className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                              data-testid="invoice-creator-gstin-input"
                            />
                          </div>
                        </div>

                        <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl p-4 space-y-3">
                          <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Client / Bill To</h4>
                          <div>
                            <Input
                              value={formData.brand_address}
                              onChange={(e) => setFormData({ ...formData, brand_address: e.target.value })}
                              placeholder="Brand address (optional)"
                              className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                              data-testid="invoice-brand-address-input"
                            />
                          </div>
                          <div>
                            <Input
                              value={formData.brand_gstin}
                              onChange={(e) => setFormData({ ...formData, brand_gstin: e.target.value })}
                              placeholder="Brand GSTIN (optional)"
                              className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                              data-testid="invoice-brand-gstin-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      className="flex-1 bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#E63E17] transition-colors"
                      data-testid="invoice-submit-button"
                    >
                      Generate & Download PDF
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowCreateDialog(false)}
                      className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6 py-2.5 font-medium"
                      data-testid="invoice-cancel-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {invoices.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-12 text-center">
              <FileText size={64} className="mx-auto mb-4 text-[#6B6B6B] dark:text-zinc-500" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">No invoices yet</h3>
              <p className="text-[#6B6B6B] dark:text-zinc-400 mb-6">Generate your first invoice PDF to track payments</p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#E63E17] transition-colors focus:ring-2 focus:ring-[#FF451A]/30"
                data-testid="empty-state-generate-invoice-button"
              >
                <Plus size={20} className="mr-2" />
                Generate Your First Invoice
              </Button>
            </div>
          ) : (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white dark:bg-[#0A0B0D] border-b border-black/[0.08] dark:border-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Invoice #</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Description</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Amount</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">GST</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Total</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] dark:text-zinc-400">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-black/[0.08] dark:border-white/5 hover:bg-[#050505]/50" data-testid="invoice-row">
                        <td className="px-6 py-4 font-medium text-[#0F0F0F] dark:text-white">{invoice.invoice_number}</td>
                        <td className="px-6 py-4 text-[#6B6B6B] dark:text-zinc-400 max-w-xs truncate">{invoice.description}</td>
                        <td className="px-6 py-4 text-[#0F0F0F] dark:text-white">₹{invoice.amount.toLocaleString()}</td>
                        <td className="px-6 py-4 text-[#0F0F0F] dark:text-white">₹{invoice.gst_amount.toLocaleString()}</td>
                        <td className="px-6 py-4 font-semibold text-[#0F0F0F] dark:text-white">₹{invoice.total_amount.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <Select
                            value={invoice.payment_status}
                            onValueChange={(value) => handleStatusChange(invoice.id, value)}
                          >
                            <SelectTrigger
                              className="w-32 px-3 py-1 rounded-full text-xs font-medium border-0"
                              style={{
                                backgroundColor: `${getStatusColor(invoice.payment_status)}15`,
                                color: getStatusColor(invoice.payment_status)
                              }}
                              data-testid="invoice-status-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="overdue">Overdue</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4 text-[#6B6B6B] dark:text-zinc-400">{new Date(invoice.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => downloadInvoicePDF(invoice.id, invoice.invoice_number)}
                            className="flex items-center gap-2 text-[#FF451A] hover:text-[#E63E17] font-medium text-sm"
                            data-testid="invoice-download-button"
                          >
                            <Download size={16} />
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
